import type {LabelSize} from '@cypro/engine/database/schema'
import {Document, Font, Image, Page, StyleSheet, Text, View} from '@react-pdf/renderer'
import {CompactLabelBody} from './compact-label-body'
import {LABEL_SIZES, PX} from './label-sizes'
import {HEADING_FONT_PX} from './substance-headings'
import type {PreparationLabelData} from './types'

// react-pdf renders a variable font at its default instance: Geist defaults to Regular (400),
// Satoshi to Black (900), so Satoshi carries the bold runs. Both cover Polish/German glyphs.
Font.register({
  family: 'Label',
  fonts: [
    {src: '/fonts/Geist-Variable.ttf', fontWeight: 'normal'},
    {src: '/fonts/Satoshi-Variable.ttf', fontWeight: 'bold'},
  ],
})
// labels must never hyphenate names or batch numbers
export const keepWordWhole = (word: string) => [word]
Font.registerHyphenationCallback(keepWordWhole)

const STANDARD = LABEL_SIZES.SIZE_100X90
const WIDTH = STANDARD.page.width * PX
const HEIGHT = STANDARD.page.height * PX
const BAND_WIDTH = STANDARD.band.width * PX

/** the PDF page of a label size, in points */
export function labelPageSize(size: LabelSize): [number, number] {
  const {page} = LABEL_SIZES[size]
  return [page.width * PX, page.height * PX]
}

// react-pdf has no tabular-figure or kerning control; a little letter spacing keeps the decimal
// point in "7.1" visible at label size (CY-398)
const styles = StyleSheet.create({
  labelBox: {
    width: WIDTH,
    height: HEIGHT,
    flexDirection: 'row',
    fontFamily: 'Label',
    color: '#000',
    backgroundColor: '#fff',
  },
  band: {width: BAND_WIDTH, borderRightWidth: STANDARD.band.line * PX, borderRightColor: '#000', position: 'relative'},
  // the rotated band text is pre-rendered (band-text.ts): react-pdf's rotated layout boxes
  // land at different offsets in the browser and in Node, clipping the text
  bandImage: {position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, objectFit: 'fill'},
  body: {
    flex: 1,
    paddingHorizontal: STANDARD.body.paddingX * PX,
    paddingTop: STANDARD.body.paddingTop * PX,
    paddingBottom: STANDARD.body.paddingBottom * PX,
  },
  patientRow: {flexDirection: 'row', justifyContent: 'space-between'},
  patientName: {fontSize: 32 * PX, fontWeight: 'bold'},
  line: {fontSize: 20 * PX, marginTop: 4 * PX, letterSpacing: 0.3},
  heading: {fontSize: 32 * PX, fontWeight: 'bold', letterSpacing: 0.35},
  administration: {fontSize: 24 * PX, marginTop: 4 * PX, letterSpacing: 0.3},
  bold: {fontWeight: 'bold'},
  singleLine: {maxLines: 1, textOverflow: 'ellipsis'},
  twoLines: {maxLines: 2, textOverflow: 'ellipsis'},
  divider: {height: 2 * PX, backgroundColor: '#000', marginVertical: 12 * PX},
  routeWarning: {
    backgroundColor: '#000',
    color: '#fff',
    fontSize: 18 * PX,
    fontWeight: 'bold',
    paddingVertical: 6 * PX,
    paddingHorizontal: 12 * PX,
    marginBottom: 8 * PX,
  },
  flags: {flexDirection: 'row', gap: 10 * PX, marginBottom: 8 * PX},
  chip: {
    borderWidth: 2 * PX,
    borderColor: '#000',
    paddingVertical: 4 * PX,
    paddingHorizontal: 12 * PX,
    fontSize: 18 * PX,
    fontWeight: 'bold',
  },
  columns: {flexDirection: 'row', gap: 24 * PX},
  caption: {fontSize: 14 * PX},
  value: {fontSize: 20 * PX, letterSpacing: 0.3},
  valueStrong: {fontSize: 24 * PX, fontWeight: 'bold', letterSpacing: 0.35},
  batches: {fontSize: 16 * PX, marginTop: 4 * PX, letterSpacing: 0.25},
  linearBarcode: {width: '100%', height: 64 * PX, marginTop: 8 * PX, objectFit: 'fill'},
  pharmacy: {
    marginTop: 'auto',
    borderTopWidth: 1 * PX,
    borderTopColor: '#000',
    paddingTop: 6 * PX,
    fontSize: 16 * PX,
    letterSpacing: 0.25,
  },
})

function Field({caption, value, strong}: {caption: string; value: string; strong?: boolean}) {
  return (
    <View>
      <Text style={styles.caption}>{caption}</Text>
      <Text style={strong ? styles.valueStrong : styles.value}>{value}</Text>
    </View>
  )
}

export interface PreparationLabelCaptions {
  useBy: string
  storage: string
  productionNo: string
  produced: string
  preparedBy: string
  administration: string
  routeWarning: string
}

export interface LabelBodyProps {
  label: PreparationLabelData
  dataMatrixSrc: string
  /** the additional Code 128 barcode (renderCode128Png); null when the setting is off */
  linearBarcodeSrc: string | null
  /** pre-rendered rotated band text (renderBandTextPng); null when the label has no band */
  bandTextSrc: string | null
  captions: PreparationLabelCaptions
}

/** the label itself at its size; the label PDF prints it per copy, the report embeds it */
export function LabelBody(props: LabelBodyProps) {
  return LABEL_SIZES[props.label.size].layout === 'compact' ? (
    <CompactLabelBody {...props} />
  ) : (
    <StandardLabelBody {...props} />
  )
}

/** the 100 × 90 mm label (Figma section 12) */
function StandardLabelBody({label, dataMatrixSrc, linearBarcodeSrc, bandTextSrc, captions}: LabelBodyProps) {
  // the name is already shortened to fit (substance-headings.ts); no line limit here, so a wrong estimate adds a
  // line instead of cutting the dose (CY-299)
  const headings = label.substanceHeadings.map(heading => (
    <Text
      key={`${heading.name} ${heading.dose}`}
      style={[styles.heading, {fontSize: HEADING_FONT_PX[label.substanceHeadingSize] * PX}]}
      hyphenationCallback={keepWordWhole}
    >
      {heading.name} {heading.dose}
    </Text>
  ))
  const productLines = label.productLines.map(line => (
    <Text key={line} style={[styles.line, styles.singleLine]}>
      {line}
    </Text>
  ))

  return (
    <View style={styles.labelBox}>
      <View style={styles.band}>{bandTextSrc && <Image src={bandTextSrc} style={styles.bandImage} />}</View>

      <View style={styles.body}>
        <View style={styles.patientRow}>
          <View style={{width: STANDARD.patientColumn * PX}}>
            <Text style={[styles.patientName, styles.singleLine]}>{label.patientName}</Text>
            <Text style={[styles.line, styles.singleLine]}>{label.patientDetailsLine}</Text>
            {label.wardLine && <Text style={[styles.line, styles.singleLine]}>{label.wardLine}</Text>}
          </View>
          <Image src={dataMatrixSrc} style={{width: STANDARD.dataMatrix * PX, height: STANDARD.dataMatrix * PX}} />
        </View>

        <View style={styles.divider} />

        {/* "Trade name before substance" swaps the two blocks: product line(s) first, then the heading(s) (CY-282) */}
        {label.productLinesFirst ? (
          <>
            {productLines}
            {headings}
          </>
        ) : (
          <>
            {headings}
            {productLines}
          </>
        )}
        {label.carrierLine && <Text style={[styles.line, styles.singleLine]}>{label.carrierLine}</Text>}
        {label.volumeLine && <Text style={[styles.line, styles.singleLine]}>{label.volumeLine}</Text>}
        {label.actualLines.map(line => (
          <Text key={line} style={[styles.line, styles.singleLine]}>
            {line}
          </Text>
        ))}
        <Text style={styles.administration}>
          {captions.administration}: <Text style={styles.bold}>{label.administrationDate}</Text>
        </Text>

        <View style={styles.divider} />

        {label.routeWarning && <Text style={styles.routeWarning}>{captions.routeWarning}</Text>}
        {label.flags.length > 0 && (
          <View style={styles.flags}>
            {label.flags.map(flag => (
              <Text key={flag} style={styles.chip}>
                {flag}
              </Text>
            ))}
          </View>
        )}
        <View style={styles.columns}>
          <View style={{width: 340 * PX}}>
            <Field caption={captions.useBy} value={label.useBy} strong />
          </View>
          {/* the storage text wraps inside the remaining width, two lines at most, so a long one never runs past
              the label edge (CY-406) */}
          <View style={{flex: 1, minWidth: 0}}>
            <Text style={styles.caption}>{captions.storage}</Text>
            <Text style={[styles.value, styles.twoLines]}>{label.storage}</Text>
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.columns}>
          <View>
            <Text style={styles.caption}>{captions.productionNo}</Text>
            <Text style={styles.value}>
              {label.productionNumber.head}
              <Text style={styles.bold}>{label.productionNumber.tail}</Text>
            </Text>
          </View>
          {label.producedAt && <Field caption={captions.produced} value={label.producedAt} />}
          {label.preparedBy && <Field caption={captions.preparedBy} value={label.preparedBy} />}
        </View>
        {label.batchesLine && <Text style={[styles.batches, styles.singleLine]}>{label.batchesLine}</Text>}

        <View style={styles.footer}>
          {linearBarcodeSrc && <Image src={linearBarcodeSrc} style={styles.linearBarcode} />}
          {label.pharmacyLine && <Text style={[styles.pharmacy, styles.singleLine]}>{label.pharmacyLine}</Text>}
        </View>
      </View>
    </View>
  )
}

export function PreparationLabelPdf(props: LabelBodyProps) {
  return (
    <Document>
      <PreparationLabelPages {...props} />
    </Document>
  )
}

/** The label pages on their own, so several labels can share one document (CY-87 reprints). */
export function PreparationLabelPages(props: LabelBodyProps) {
  return (
    <>
      {Array.from({length: props.label.copies}, (_, copy) => (
        <Page key={copy} size={labelPageSize(props.label.size)} wrap={false}>
          <LabelBody {...props} />
        </Page>
      ))}
    </>
  )
}
